import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { sortBy, uniqBy } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { PositionChangesReportData, Positions } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { convertToDateOrUndefined, getEnumName } from '~/utils/helper';

type PositionChangesReportEntry = {
  rowId: string;
  employeeId: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  positionName: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  status: string;
  hasAccount: boolean;
};

type PropsType = {
  data: PositionChangesReportData[];
  startDate: Date;
  endDate: Date;
};

export const usePositionChangesReport = (props: PropsType) => {
  const { data, startDate, endDate } = props;
  const { t } = useTranslation();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const { getEmployeeById } = useAllEmployees();
  const { getEnums } = useAllEnums();
  const positionEnums = useMemo(() => {
    return getEnums(EnumListKey.EMPLOYEE_POSITION);
  }, [getEnums]);

  const positionStatusOptions = useMemo(() => {
    return {
      startedEmployment: t('laStartedEmp'),
      startedAndEndedEmployemnt: `${t('laStartedEmp')}, ${t('laEndedEmpl')}`,
      endedEmployment: t('laEndedEmpl'),
      positionAndEndedEmployment: `${t('laPosition')}, ${t('laEndedEmpl')}`,
      position: t('laPosition'),
    };
  }, [t]);

  const { rows, reportPositionStatusOptions } = useMemo(() => {
    if (!data) {
      return { rows: [], reportPositionStatusOptions: [] };
    }

    const rangeStart = startDate;
    const rangeEnd = endDate;

    const isWithinRangeInclusive = (date?: string | Date | null) => {
      if (!date) return false;
      return moment(date).isBetween(rangeStart, rangeEnd, 'date', '[]');
    };

    const resolvePositionStatus = (
      positionsCount: number,
      index: number,
      position: Pick<PositionChangesReportEntry, 'startDate' | 'endDate'>,
    ): string => {
      const { startDate, endDate } = position;

      const isFirst = index === 0;
      const isLast = positionsCount - 1 === index;
      const hasEndedInRange =
        isLast &&
        !!endDate &&
        moment(endDate).isSameOrBefore(moment(rangeEnd), 'date');

      if (isFirst) {
        return hasEndedInRange
          ? positionStatusOptions.startedAndEndedEmployemnt
          : positionStatusOptions.startedEmployment;
      }

      if (hasEndedInRange) {
        return isWithinRangeInclusive(startDate)
          ? positionStatusOptions.positionAndEndedEmployment
          : positionStatusOptions.endedEmployment;
      }

      return positionStatusOptions.position;
    };

    const reportPositionChanges = data.flatMap(
      (entry, entryIndex): PositionChangesReportEntry[] => {
        const {
          fEmpId,
          fEmpFirstName,
          fEmpLastName,
          fEmpPositions,
          fEmpHasAccount,
        } = entry;

        const emp = getEmployeeById(fEmpId);
        const employeeNumber = emp?.fEmpPersonNumber || '';

        const employeeDetails = {
          employeeId: fEmpId,
          firstName: fEmpFirstName,
          lastName: fEmpLastName,
          employeeNumber,
          hasAccount: fEmpHasAccount,
        };

        const noChangesRow: PositionChangesReportEntry = {
          rowId: `${fEmpId}-${entryIndex}-noChanges`,
          ...employeeDetails,
          positionName: '',
          startDate: undefined,
          endDate: undefined,
          status: '',
        };

        if (!fEmpPositions || fEmpPositions.length === 0) {
          return [noChangesRow];
        }

        const positions = sortBy(
          fEmpPositions.map((position: Positions) => {
            const {
              fEmpPrevPosition,
              fEmpPrevPositionStartDate,
              fEmpPrevPositionEndDate,
            } = position;

            return {
              positionName: getEnumName(positionEnums, fEmpPrevPosition),
              startDate: convertToDateOrUndefined(fEmpPrevPositionStartDate),
              endDate: convertToDateOrUndefined(fEmpPrevPositionEndDate),
            };
          }),
          ['startDate'],
        );

        const positionCount = positions.length;
        const employeePositionChanges = positions.map(
          (position, positionIndex): PositionChangesReportEntry => {
            return {
              rowId: `${fEmpId}-${entryIndex}-${positionIndex}`,
              ...employeeDetails,
              ...position,
              status: resolvePositionStatus(
                positionCount,
                positionIndex,
                position,
              ),
            };
          },
        );

        const positionChangesInRange = employeePositionChanges.filter(
          ({ startDate, endDate }) => {
            return (
              isWithinRangeInclusive(startDate) ||
              isWithinRangeInclusive(endDate)
            );
          },
        );

        if (positionChangesInRange.length > 0) {
          return positionChangesInRange;
        }

        return [noChangesRow];
      },
    );

    const reportPositionStatusOptions = uniqBy(reportPositionChanges, 'status')
      .map((pc) => pc.status)
      .filter((status) => !!status);

    return {
      rows: reportPositionChanges,
      reportPositionStatusOptions,
    };
  }, [
    data,
    startDate,
    endDate,
    getEmployeeById,
    positionEnums,
    positionStatusOptions,
  ]);

  const { columnConfigs, columnGroupingModel } = useMemo(() => {
    const employeeColumnConfigs: GridColDef<PositionChangesReportEntry>[] = [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => (
          <EmployeeCellComponent
            {...params}
            pictureOnly={true}
            onClick={() => {
              goToEmployeeBasicDataView(params.row.employeeId);
            }}
          />
        ),
        minWidth: 60,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => {
              goToEmployeeBasicDataView(params.row.employeeId);
            }}
          />
        ),
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => {
              goToEmployeeBasicDataView(params.row.employeeId);
            }}
          />
        ),
        minWidth: 110,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
      },
    ];
    const positionColumnConfigs: GridColDef<PositionChangesReportEntry>[] = [
      {
        field: 'positionName',
        headerName: t('laPosition'),
        minWidth: 200,
        rowSpanValueGetter: () => null,
      },
      {
        field: 'startDate',
        headerName: t('laStartDate'),
        type: 'date',
        minWidth: 120,
        valueFormatter: dateCellValueFormatter,
        rowSpanValueGetter: () => null,
      },
      {
        field: 'endDate',
        headerName: t('laEndDate'),
        type: 'date',
        minWidth: 120,
        valueFormatter: dateCellValueFormatter,
        rowSpanValueGetter: () => null,
      },
      {
        field: 'status',
        headerName: t('laStatus'),
        minWidth: 200,
        type: 'singleSelect',
        valueOptions: Object.values(reportPositionStatusOptions),
        rowSpanValueGetter: () => null,
      },
    ];

    const columnConfigs: GridColDef<PositionChangesReportEntry>[] = [
      ...employeeColumnConfigs,
      ...positionColumnConfigs,
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'position',
        headerName: t('laPosition'),
        children: getFieldNamesFromColumnConfig(positionColumnConfigs),
      },
    ];

    return { columnConfigs, columnGroupingModel };
  }, [t, goToEmployeeBasicDataView, reportPositionStatusOptions]);

  return {
    table: {
      columnConfigs,
      columnGroupingModel,
      rows,
    },
  };
};
